const logError = (message: string) => {
    console.error(`[性能测试报告] ${message}`);
};

const testReactDevtoolsAgentValid = () => {
    const reactDevTools = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
    const reactDevtoolsAgent = reactDevTools?.reactDevtoolsAgent;
    if (!reactDevTools) {
        logError('没有安装reactDevtools插件， 请安装后再试');
        return false;
    }
    if (!reactDevtoolsAgent) {
        logError('reactDevtoolsAgent失效，请刷新重试');
        return false;
    }
    return true;
};

export const reloadAndProfile = () => {
    if (testReactDevtoolsAgentValid()) {
        const reactDevTools = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
        const reactDevtoolsAgent = reactDevTools?.reactDevtoolsAgent;
        reactDevtoolsAgent.reloadAndProfile(true); // 传 true 可获取组件渲染原因
    }
};


export const stopProfiling = () => {
    // 停止 profiler 数据获取
    if (testReactDevtoolsAgentValid()) {
        const reactDevTools = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
        const reactDevtoolsAgent = reactDevTools?.reactDevtoolsAgent;
        if (reactDevtoolsAgent._isProfiling) {
            reactDevtoolsAgent.stopProfiling();
        } else {
            logError('没有开启记录，请执行开始记录后再停止');
        }
    }
};


/**
 * 由于 hook 变化导致 rerender 返回信息处理
 * @param {*} indices
 * @returns
 */
function hookIndicesToString(indices: any[]) {
    const numbers = indices.map(value => value + 1);

    switch (numbers.length) {
        case 0:
            return 'No hooks changed';
        case 1:
            return `Hook ${numbers[0]} changed`;
        case 2:
            return `Hooks ${numbers[0]} and ${numbers[1]} changed`;
        default:
            return `Hooks ${numbers.slice(0, numbers.length - 1).join(', ')} and ${
                numbers[numbers.length - 1]
            } changed`;
    }
}

/**
 * 获取渲染原因
 * @param {*} changeDescription 渲染原因信息
 * @returns
 */
interface IChangeDescription {
    context: any;
    didHooksChange: any;
    hooks: any;
    isFirstMount: any;
    props: any;
    state: any;
}
function getRenderReason(changeDescription: IChangeDescription | null) {
    if (changeDescription == null) {
        return '';
    }

    const {
        context,
        didHooksChange,
        hooks,
        isFirstMount,
        props,
        state,
    } = changeDescription;

    if (isFirstMount) {
        return 'This is the first time the component rendered.';
    }

    const changes = [];

    if (context === true) {
        changes.push('Content changed');
    } else if (
        typeof context === 'object' &&
        context !== null &&
        context.length !== 0
    ) {
        changes.push(`Content changed:(${context.join(',')})`);
    }

    if (didHooksChange) {
        if (Array.isArray(hooks)) {
            changes.push(hookIndicesToString(hooks));
        } else {
            changes.push('Hooks changed');
        }
    }

    if (props !== null && props.length !== 0) {
        changes.push(`Props changed:(${props.join(',')})`);
    }

    if (state !== null && state.length !== 0) {
        changes.push(`State changed:(${state.join(',')})`);
    }

    if (changes.length === 0) {
        return 'The parent component rendered.';
    }

    return changes.length > 1 ? changes : changes[0];
}

interface ICommitData {
    duration: any;
    fiberActualDurations: any;
    changeDescriptions: any;
    renderReasonMap?: Map<any, any> | undefined;
}

export const getProfileData = () => {
    const reactDevTools = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;
    const reactDevtoolsAgent = reactDevTools?.reactDevtoolsAgent;
    if (!testReactDevtoolsAgentValid()) {
        return;
    }
    // 获取 profiler 数据
    const rendererInterfaces = reactDevTools.rendererInterfaces.get(1);

    const profilingData = rendererInterfaces.getProfilingData();

    // 获取组件名方法，根据 FiberID 获取
    const getDisplayNameForFiberID = rendererInterfaces.getDisplayNameForFiberID;
    // 获取组件 rendered by 信息
    const getOwnersList = rendererInterfaces.getOwnersList;

    let renderDurationSum = 0; // 渲染时间总计
    const renderMap = new Map(); // 各个组件渲染数据

    const appCommitData = profilingData.dataForRoots[0].commitData || [];

    // 遍历所有 commit 数据
    appCommitData.forEach((commitData: ICommitData) => {
        const {
            duration,
            fiberActualDurations,
            changeDescriptions,
            renderReasonMap = new Map(),
        } = commitData;

        renderDurationSum += duration;

        // 遍历获取组件渲染原因
        changeDescriptions.forEach((item: [any, any]) => {
            const [fiberID, changeDescription] = item;
            renderReasonMap.set(fiberID, getRenderReason(changeDescription));
        });

        fiberActualDurations.forEach((item: [any, any]) => {
            const [fiberID, componentDuration] = item;
            const componentName = getDisplayNameForFiberID(fiberID);
            const key = `${fiberID}-${componentName}`;

            const componentRenderData = renderMap.get(key);
            const newRenderData = {
                duration: componentDuration,
                reason: renderReasonMap.get(fiberID) || '',
            };
            if (componentRenderData) {
                componentRenderData.push(newRenderData);
            } else {
                renderMap.set(key, [newRenderData]);
            }
        });
    });

    // 输出结果转换为数组
    const renderMapArray = [...renderMap];

    // 输出结果增加组件 rendered by 信息
    renderMapArray.forEach(item => {
        const [key] = item;
        const fiberID = +key.split('-')[0];
        const ownerList = getOwnersList(fiberID);

        if (Array.isArray(ownerList)) {
            const owners = ownerList.reduce((accumulator: string, currentValue: any) => {
                return `${accumulator}${accumulator ? `,${currentValue.displayName}` : currentValue.displayName}`;
            }, '');
            item[2] = owners;
        }
    });

    return {
        renderDurationSum,
        renderMap: renderMapArray,
    };
};

